/**
 * Diseño de sonrisa sobre una foto de frente: medidas, proporción áurea o RED y plantilla de dientes.
 * Todo es cálculo exacto sobre los puntos que marca el profesional (nada lo inventa una IA): cada número se puede revisar.
 * Las proporciones son una guía de diseño, no una regla: el profesional decide.
 *
 * Convenciones: coordenadas en píxeles de la foto (y crece hacia abajo). «Derecha» e «izquierda» son las del paciente:
 * la derecha del paciente queda a la izquierda de la imagen. Las piezas van de izquierda a derecha de la imagen: 13, 12, 11, 21, 22, 23.
 */
import { TIPOS_ROSTRO, type TipoRostro } from "./rostro.ts";

export interface Punto { x: number; y: number }

export interface PuntosSonrisa {
  /** pupila derecha del paciente (la de la izquierda en la foto) e izquierda */
  pupilaDer: Punto; pupilaIzq: Punto;
  /** línea media de la cara (por ejemplo, entre las cejas y el mentón) */
  medioArriba: Punto; medioAbajo: Punto;
  /** 7 contactos de los seis dientes anteriores, de izquierda a derecha en la foto: distal 13, 13-12, 12-11, línea media 11-21, 21-22, 22-23, distal 23 */
  contactos: Punto[];
  /** punto medio del borde del incisivo central y de su margen de la encía (cuello) */
  bordeCentral: Punto; cuelloCentral: Punto;
}

export const DIENTES_SONRISA = ["13", "12", "11", "21", "22", "23"] as const;
export type MetodoSonrisa = "aurea" | "red";
export type FormaDiente = "cuadrada" | "ovoide" | "triangular";
export const FORMAS_DIENTE: readonly [FormaDiente, string][] = [["cuadrada", "Cuadrada"], ["ovoide", "Ovoide"], ["triangular", "Triangular"]];
/** Proporción áurea: cada diente es 0,618 del anterior (central 1, lateral 0,618, canino 0,382). */
export const RAZON_AUREA = 0.618;
/** RED («proporción dental recurrente»): cada diente mide un porcentaje fijo del anterior; lo habitual es 62 a 80 %. */
export const RED_MINIMO = 0.62, RED_MAXIMO = 0.85, RED_HABITUAL = 0.7;

export interface OpcionesSonrisa {
  metodo: MetodoSonrisa;
  /** razón de la RED (0,62 a 0,85); con «aurea» se ignora */
  red: number;
  forma: FormaDiente;
  /** ancho / alto deseado del incisivo central (0,70 a 0,90; lo habitual 0,75 a 0,80) */
  relacionAnchoAlto: number;
  /** distancia entre pupilas en mm: da la escala a la foto (promedio 62 a 65 mm; mejor medirla al paciente) */
  interpupilarMm: number;
  /** de dónde parte el diseño: de la línea media de la cara o de la línea media dental actual */
  centrar: "facial" | "dental";
  /** tipo de rostro (lo calcula Donna con la foto o lo elige el profesional); vacío si no se determinó */
  rostro: TipoRostro | "";
}
export const OPCIONES_SONRISA: OpcionesSonrisa = { metodo: "aurea", red: RED_HABITUAL, forma: "ovoide", relacionAnchoAlto: 0.8, interpupilarMm: 63, centrar: "facial", rostro: "" };

const num = (v: unknown) => typeof v === "number" && Number.isFinite(v);
const esPunto = (v: unknown): v is Punto => !!v && typeof v === "object" && num((v as Punto).x) && num((v as Punto).y) && Math.abs((v as Punto).x) < 20000 && Math.abs((v as Punto).y) < 20000;
const redondear = (n: number, d = 1) => { const f = 10 ** d; return Math.round(n * f) / f; };

/** Revisa lo que llega del navegador. Devuelve los puntos limpios o la lista de problemas. */
export function leerPuntosSonrisa(entrada: unknown): { puntos: PuntosSonrisa | null; problemas: string[] } {
  const o = (entrada && typeof entrada === "object" ? entrada : {}) as Record<string, unknown>;
  const e: string[] = [];
  const un = (k: string, n: string): Punto => { const v = o[k]; if (!esPunto(v)) { e.push(`Falta marcar ${n}.`); return { x: 0, y: 0 }; } return { x: v.x, y: v.y }; };
  const p: PuntosSonrisa = {
    pupilaDer: un("pupilaDer", "la pupila derecha"), pupilaIzq: un("pupilaIzq", "la pupila izquierda"),
    medioArriba: un("medioArriba", "la línea media (arriba)"), medioAbajo: un("medioAbajo", "la línea media (abajo)"),
    contactos: [], bordeCentral: un("bordeCentral", "el borde del incisivo central"), cuelloCentral: un("cuelloCentral", "el cuello del incisivo central"),
  };
  const c = o.contactos;
  if (!Array.isArray(c) || c.length !== 7 || !c.every(esPunto)) e.push("Marca los 7 puntos de contacto de los dientes anteriores.");
  else p.contactos = c.map((q: Punto) => ({ x: q.x, y: q.y }));
  if (e.length) return { puntos: null, problemas: e };
  if (distancia(p.pupilaDer, p.pupilaIzq) < 40) e.push("Las pupilas están demasiado juntas: revisa su posición.");
  if (p.pupilaIzq.x <= p.pupilaDer.x) e.push("La pupila derecha del paciente debe quedar a la izquierda de la foto.");
  const { u } = ejes(p);
  for (let i = 1; i < 7; i++) if ((p.contactos[i].x - p.contactos[i - 1].x) * u.x + (p.contactos[i].y - p.contactos[i - 1].y) * u.y < 4) { e.push("Los puntos de contacto deben ir ordenados de izquierda a derecha."); break; }
  return e.length ? { puntos: null, problemas: e } : { puntos: p, problemas: [] };
}

export function leerOpcionesSonrisa(entrada: unknown): OpcionesSonrisa {
  const o = (entrada && typeof entrada === "object" ? entrada : {}) as Record<string, unknown>;
  const rango = (v: unknown, min: number, max: number, def: number) => (num(v) ? Math.min(max, Math.max(min, v as number)) : def);
  return {
    metodo: o.metodo === "red" ? "red" : "aurea",
    red: rango(o.red, RED_MINIMO, RED_MAXIMO, RED_HABITUAL),
    forma: o.forma === "cuadrada" || o.forma === "triangular" ? o.forma : "ovoide",
    relacionAnchoAlto: rango(o.relacionAnchoAlto, 0.7, 0.9, 0.8),
    interpupilarMm: rango(o.interpupilarMm, 50, 80, 63),
    centrar: o.centrar === "dental" ? "dental" : "facial",
    rostro: (TIPOS_ROSTRO as readonly unknown[]).includes(o.rostro) ? (o.rostro as TipoRostro) : "",
  };
}

const distancia = (a: Punto, b: Punto) => Math.hypot(b.x - a.x, b.y - a.y);

/** Ejes de la sonrisa: u sigue la línea de las pupilas; v es perpendicular y apunta hacia abajo. */
function ejes(p: PuntosSonrisa) {
  const dx = p.pupilaIzq.x - p.pupilaDer.x, dy = p.pupilaIzq.y - p.pupilaDer.y, d = Math.hypot(dx, dy) || 1;
  const u = { x: dx / d, y: dy / d };
  return { u, v: { x: -u.y, y: u.x }, angulo: (Math.atan2(dy, dx) * 180) / Math.PI };
}
const punto = (a: Punto, b: Punto, u: Punto) => (b.x - a.x) * u.x + (b.y - a.y) * u.y;

/** Punto donde la línea media de la cara cruza la línea del borde incisal (paralela a las pupilas). */
function medioFacialEnBorde(p: PuntosSonrisa): Punto {
  const { u, v } = ejes(p);
  const d = { x: p.medioAbajo.x - p.medioArriba.x, y: p.medioAbajo.y - p.medioArriba.y };
  const alturaBorde = p.bordeCentral.x * v.x + p.bordeCentral.y * v.y;
  const alturaArriba = p.medioArriba.x * v.x + p.medioArriba.y * v.y;
  const dv = d.x * v.x + d.y * v.y;
  const t = Math.abs(dv) < 1e-6 ? 0 : (alturaBorde - alturaArriba) / dv;
  void u;
  return { x: p.medioArriba.x + d.x * t, y: p.medioArriba.y + d.y * t };
}

export interface MedidaDiente { pieza: string; actualMm: number; propuestoMm: number }
export interface AnalisisSonrisa {
  mmPorPx: number;
  inclinacionPupilas: number;            // grados respecto a la horizontal de la foto
  inclinacionPlanoIncisal: number;       // grados respecto a la línea de las pupilas (0 = paralelo)
  dientes: MedidaDiente[];
  anchoTotalMm: number;
  centralActual: { anchoMm: number; altoMm: number; relacion: number };
  centralPropuesto: { anchoMm: number; altoMm: number; relacion: number };
  proporcionActual: { lateralCentral: number; caninoLateral: number };
  razonPropuesta: number;                // lo que se propone (0,618 si es áurea; la RED elegida)
  desvioLineaMediaMm: number;            // dental respecto a la facial: + hacia la izquierda del paciente
  observaciones: string[];
}

const coma = (n: number, d = 1) => redondear(n, d).toString().replace(".", ",");

/** Anchos propuestos de los seis dientes (13, 12, 11, 21, 22, 23) para un ancho total dado, con razón constante entre un diente y el siguiente. */
export function anchosPropuestos(anchoTotal: number, razon: number): number[] {
  const central = anchoTotal / (2 * (1 + razon + razon * razon));
  const l = central * razon, k = l * razon;
  return [k, l, central, central, l, k];
}

export function analizarSonrisa(p: PuntosSonrisa, entrada: Partial<OpcionesSonrisa> = {}): AnalisisSonrisa {
  const o = leerOpcionesSonrisa({ ...OPCIONES_SONRISA, ...entrada });
  const { u, v, angulo } = ejes(p);
  const mm = o.interpupilarMm / distancia(p.pupilaDer, p.pupilaIzq);
  const anchos = [0, 1, 2, 3, 4, 5].map((i) => punto(p.contactos[i], p.contactos[i + 1], u));
  const total = anchos.reduce((a, b) => a + b, 0);
  const razon = o.metodo === "aurea" ? RAZON_AUREA : o.red;
  const prop = anchosPropuestos(total, razon);
  const centralAncho = (anchos[2] + anchos[3]) / 2;
  const centralAlto = Math.max(1, punto(p.cuelloCentral, p.bordeCentral, v));
  const propAncho = prop[2], propAlto = propAncho / o.relacionAnchoAlto;
  const lc = ((anchos[1] / anchos[2]) + (anchos[4] / anchos[3])) / 2, cl = ((anchos[0] / anchos[1]) + (anchos[5] / anchos[4])) / 2;
  const inicio = p.contactos[0], fin = p.contactos[6];
  const planoIncisal = (Math.atan2(fin.y - inicio.y, fin.x - inicio.x) * 180) / Math.PI;
  const medio = medioFacialEnBorde(p);
  const desvio = ((p.contactos[3].x - medio.x) * u.x + (p.contactos[3].y - medio.y) * u.y) * mm;

  const obs: string[] = [];
  if (Math.abs(angulo) > 3) obs.push(`La línea de las pupilas está inclinada ${coma(Math.abs(angulo))}° respecto a la foto: la cabeza pudo estar ladeada. Conviene repetir la foto con la cabeza recta.`);
  const dif = planoIncisal - angulo;
  if (Math.abs(dif) > 2) obs.push(`El plano de los bordes de los dientes no es paralelo a la línea de las pupilas (${coma(Math.abs(dif))}° de diferencia): revisa si hay que corregirlo en el diseño.`);
  if (Math.abs(desvio) >= 1) obs.push(`La línea media dental está ${coma(Math.abs(desvio))} mm hacia la ${desvio > 0 ? "izquierda" : "derecha"} del paciente respecto a la línea media de la cara.`);
  else obs.push("La línea media dental coincide con la de la cara (menos de 1 mm de diferencia).");
  const razonTxt = o.metodo === "aurea" ? "áurea (62 %)" : `RED (${Math.round(o.red * 100)} %)`;
  obs.push(`Hoy el lateral mide ${Math.round(lc * 100)} % del central y el canino ${Math.round(cl * 100)} % del lateral. Con la proporción ${razonTxt} serían ${Math.round(razon * 100)} % y ${Math.round(razon * 100)} %.`);
  const rel = centralAncho / centralAlto;
  obs.push(rel < 0.7 ? `El central es largo y angosto (ancho ${Math.round(rel * 100)} % del alto; lo habitual es 75 a 80 %).` : rel > 0.9 ? `El central es ancho y corto (ancho ${Math.round(rel * 100)} % del alto; lo habitual es 75 a 80 %).` : `El ancho del central es ${Math.round(rel * 100)} % de su alto (lo habitual es 75 a 80 %).`);
  if (o.interpupilarMm === 63) obs.push("La escala en milímetros usa 63 mm de distancia entre pupilas (promedio). Para medidas más exactas, mide la distancia real del paciente y escríbela.");

  return {
    mmPorPx: mm, inclinacionPupilas: redondear(angulo), inclinacionPlanoIncisal: redondear(planoIncisal - angulo),
    dientes: DIENTES_SONRISA.map((pieza, i) => ({ pieza, actualMm: redondear(anchos[i] * mm), propuestoMm: redondear(prop[i] * mm) })),
    anchoTotalMm: redondear(total * mm),
    centralActual: { anchoMm: redondear(centralAncho * mm), altoMm: redondear(centralAlto * mm), relacion: redondear(rel, 2) },
    centralPropuesto: { anchoMm: redondear(propAncho * mm), altoMm: redondear(propAlto * mm), relacion: redondear(o.relacionAnchoAlto, 2) },
    proporcionActual: { lateralCentral: redondear(lc, 2), caninoLateral: redondear(cl, 2) },
    razonPropuesta: razon, desvioLineaMediaMm: redondear(desvio), observaciones: obs,
  };
}

export interface PiezaPlantilla { pieza: string; /** contorno listo para dibujar (SVG) en píxeles de la foto */ camino: string; anchoMm: number; altoMm: number }

/** Plantilla de los seis dientes propuestos, centrada en la línea media elegida y alineada con la línea de las pupilas. */
export function plantillaSonrisa(p: PuntosSonrisa, entrada: Partial<OpcionesSonrisa> = {}): PiezaPlantilla[] {
  const o = leerOpcionesSonrisa({ ...OPCIONES_SONRISA, ...entrada });
  const { u, v } = ejes(p);
  const a = analizarSonrisa(p, o);
  const mm = a.mmPorPx;
  const pxDe = (m: number) => m / mm;
  const ancho = anchosPropuestos(pxDe(a.anchoTotalMm), a.razonPropuesta);
  const origen: Punto = o.centrar === "facial" ? medioFacialEnBorde(p) : (() => {
    const c = p.contactos[3]; const dv = (p.bordeCentral.x - c.x) * v.x + (p.bordeCentral.y - c.y) * v.y;
    return { x: c.x + v.x * dv, y: c.y + v.y * dv };
  })();
  const hCentral = pxDe(a.centralPropuesto.altoMm);
  // bordes y cuellos respecto al borde del central (hacia arriba, en mm): el lateral queda 1 mm más corto y su encía 1 mm más baja; el canino casi a la altura del central
  const borde = [0.3, 1.0, 0, 0, 1.0, 0.3].map(pxDe);
  const cuello = [hCentral, hCentral - pxDe(1.0), hCentral, hCentral, hCentral - pxDe(1.0), hCentral];
  const limites: number[] = [];
  let acum = -(ancho[0] + ancho[1] + ancho[2]);
  limites.push(acum);
  for (const w of ancho) { acum += w; limites.push(acum); }
  const f = (n: number) => redondear(n, 1);
  const T = (uu: number, vv: number) => `${f(origen.x + u.x * uu + v.x * vv)} ${f(origen.y + u.y * uu + v.y * vv)}`;
  const forma = { cuadrada: { cuello: 0.8, esquina: 0.1 }, ovoide: { cuello: 0.68, esquina: 0.3 }, triangular: { cuello: 0.52, esquina: 0.06 } }[o.forma];
  return DIENTES_SONRISA.map((pieza, i) => {
    const w = (limites[i + 1] - limites[i]) * 0.985, x0 = (limites[i] + limites[i + 1]) / 2;
    const ve = -borde[i], vc = -cuello[i], h = ve - vc;
    const cw = (w * forma.cuello) / 2, mw = w / 2, rr = w * forma.esquina;
    const camino = [
      `M ${T(x0 - cw, vc)}`,
      `Q ${T(x0, vc - 0.16 * w)} ${T(x0 + cw, vc)}`,
      `C ${T(x0 + cw + (mw - cw) * 0.8, vc + 0.3 * h)} ${T(x0 + mw, ve - 0.45 * h)} ${T(x0 + mw - rr * 0.35, ve - rr * 0.1)}`,
      `Q ${T(x0 + mw - rr * 0.5, ve + 0.02 * w)} ${T(x0 + mw - rr, ve + 0.03 * w)}`,
      `Q ${T(x0, ve + 0.06 * w)} ${T(x0 - mw + rr, ve + 0.03 * w)}`,
      `Q ${T(x0 - mw + rr * 0.5, ve + 0.02 * w)} ${T(x0 - mw + rr * 0.35, ve - rr * 0.1)}`,
      `C ${T(x0 - mw, ve - 0.45 * h)} ${T(x0 - cw - (mw - cw) * 0.8, vc + 0.3 * h)} ${T(x0 - cw, vc)}`,
      "Z",
    ].join(" ");
    return { pieza, camino, anchoMm: redondear(ancho[i] * mm), altoMm: redondear((cuello[i] - borde[i]) * mm) };
  });
}

/** Posiciones iniciales de los puntos para una foto de ese tamaño (el profesional los arrastra a su lugar). */
export function puntosIniciales(ancho: number, alto: number): PuntosSonrisa {
  // proporciones de una cara real: pupilas separadas ≈ 24 % del ancho de la foto; los seis dientes miden ≈ 76 % de esa distancia y el central ≈ 17 % de alto
  const cx = ancho / 2, ys = alto * 0.62, sem = ancho * 0.24 * 0.38;
  const xs = [-1, -0.62, -0.26, 0, 0.26, 0.62, 1].map((k) => cx + k * sem);
  return {
    pupilaDer: { x: cx - ancho * 0.12, y: alto * 0.36 }, pupilaIzq: { x: cx + ancho * 0.12, y: alto * 0.36 },
    medioArriba: { x: cx, y: alto * 0.28 }, medioAbajo: { x: cx, y: alto * 0.9 },
    contactos: xs.map((x) => ({ x, y: ys })),
    bordeCentral: { x: cx, y: ys + alto * 0.006 }, cuelloCentral: { x: cx, y: ys - ancho * 0.041 },
  };
}

export const AVISO_DISENO_SONRISA = "Diseño orientativo: las proporciones son una guía y el resultado final depende del criterio clínico y de lo que el paciente decida. No es una promesa de resultado.";

/** Lo que el reconocimiento de cara del navegador encuentra en la foto, en píxeles. */
export interface MarcasCara {
  irisDer: Punto; irisIzq: Punto;              // centro de cada pupila (en la foto: «der» = la de la izquierda de la imagen)
  nasion: Punto; menton: Punto;                // entre las cejas / punta del mentón
  comisuraDer: Punto; comisuraIzq: Punto;      // esquinas internas de la boca
  labioSup: Punto; labioInf: Punto;            // borde interno del labio de arriba y de abajo, al centro
}
export interface ResultadoCara { puntos: PuntosSonrisa; /** si la boca estaba abierta, los dientes se ubican según los labios; si no, se estiman */ dientesPorLabios: boolean }

/** Posiciona los 13 puntos de la sonrisa a partir de las marcas de la cara; los de los dientes son una estimación que el profesional ajusta. */
export function puntosDesdeCara(m: MarcasCara): ResultadoCara {
  const [izq, der] = m.irisDer.x <= m.irisIzq.x ? [m.irisDer, m.irisIzq] : [m.irisIzq, m.irisDer];
  const dp = distancia(izq, der);
  const base: PuntosSonrisa = { pupilaDer: izq, pupilaIzq: der, medioArriba: m.nasion, medioAbajo: m.menton, contactos: [], bordeCentral: m.labioInf, cuelloCentral: m.labioSup };
  const { u, v } = ejes(base);
  const aperturaV = (m.labioInf.x - m.labioSup.x) * v.x + (m.labioInf.y - m.labioSup.y) * v.y;
  const abierta = aperturaV > 0.035 * dp;
  const anchoBoca = distancia(m.comisuraDer, m.comisuraIzq);
  const ancho = abierta ? Math.min(0.85 * anchoBoca, 0.8 * dp) : 0.76 * dp;
  const alto = 0.17 * dp;
  // el borde de los incisivos queda cerca del borde del labio de abajo si se ve la sonrisa; si no, un poco bajo el labio de arriba
  const yBorde = abierta ? (m.labioInf.x * v.x + m.labioInf.y * v.y) - 0.05 * dp : (m.labioSup.x * v.x + m.labioSup.y * v.y) + 0.05 * dp;
  // línea media de la cara a esa altura
  const dxm = m.menton.x - m.nasion.x, dym = m.menton.y - m.nasion.y;
  const hN = m.nasion.x * v.x + m.nasion.y * v.y, dv = dxm * v.x + dym * v.y;
  const t = Math.abs(dv) < 1e-6 ? 0 : (yBorde - hN) / dv;
  const centro = { x: m.nasion.x + dxm * t, y: m.nasion.y + dym * t };
  const sem = ancho / 2;
  // anchos aparentes habituales: central 1, lateral 0,75, canino 0,65 (cada uno hacia afuera de la línea media)
  const fr = [0, 1 / 2.4, 1.75 / 2.4, 1];
  const desplazamientos = [-fr[3], -fr[2], -fr[1], 0, fr[1], fr[2], fr[3]];
  const alturaContacto = -0.12 * alto;
  base.contactos = desplazamientos.map((k) => ({ x: centro.x + u.x * k * sem + v.x * alturaContacto, y: centro.y + u.y * k * sem + v.y * alturaContacto }));
  base.bordeCentral = { x: centro.x, y: centro.y };
  base.cuelloCentral = { x: centro.x - v.x * alto, y: centro.y - v.y * alto };
  return { puntos: base, dientesPorLabios: abierta };
}

export interface Caja { x: number; y: number; w: number; h: number }
/** Proporción del recorte que se envía a la IA de imágenes (ancho / alto). */
export const ASPECTO_RECORTE = 4 / 3;

/**
 * Recorte de la parte baja de la cara (de la nariz al mentón, sin los ojos) con proporción 4:3, centrado en la boca y dentro de la imagen.
 * Es lo único que se envía a la IA de imágenes: así no sale la cara completa.
 */
export function recorteBoca(m: { comisuraDer: Punto; comisuraIzq: Punto; nariz: Punto; menton: Punto }, anchoImg: number, altoImg: number, aspecto = ASPECTO_RECORTE): Caja {
  const bocaW = distancia(m.comisuraDer, m.comisuraIzq);
  const altoCara = Math.abs(m.menton.y - m.nariz.y);
  let w = Math.max(bocaW * 2.3, altoCara * 1.25 * aspecto);
  w = Math.min(w, anchoImg, altoImg * aspecto);
  const h = w / aspecto;
  const cx = (m.comisuraDer.x + m.comisuraIzq.x) / 2, cy = (m.nariz.y + m.menton.y) / 2;
  const x = Math.min(Math.max(cx - w / 2, 0), anchoImg - w), y = Math.min(Math.max(cy - h / 2, 0), altoImg - h);
  return { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) };
}
/** Recorte de partida si no se encontró la cara: la parte baja y central de la foto (el profesional lo mueve). */
export function recorteInicial(anchoImg: number, altoImg: number, aspecto = ASPECTO_RECORTE): Caja {
  const w = Math.min(anchoImg * 0.6, altoImg * 0.4 * aspecto), h = w / aspecto;
  return { x: Math.round((anchoImg - w) / 2), y: Math.round(Math.min(altoImg - h, altoImg * 0.5)), w: Math.round(w), h: Math.round(h) };
}
